import React, { useState, useEffect } from 'react';
import Linkify from 'react-linkify';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import Avatar from '../avatar';
import { getIcon } from './chat-alert-icon';
import { ChatBubbleSC, TChatBubbleSC } from './chat-bubble.styled';
import { actions as appActions } from '../../store/ducks/app';
import { AttachmentModel } from '../../lib/db';
import { getTimeFormat } from '../../helpers/utils-helpers';

type TProps = {
  from: string;
  text: string;
  stamp?: string;
  avatarUrl?: string;
  alertVital?: string;
  attachment?: {
    id: string | null;
    type: string | null;
    size: string | null;
    name: string | null;
  };
} & TChatBubbleSC;

const ChatBubble: React.FC<TProps> = ({
  from,
  text,
  isMe,
  isSequence,
  avatarUrl,
  alertVital,
  stamp,
  styleType,
  attachment,
}) => {
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [objectUrl, setObjectUrl] = useState('');

  const dispatch = useDispatch();

  useEffect(() => {
    if (attachment && attachment.id) {
      AttachmentModel.processAttachment({
        ...attachment,
        id: attachment.id || '',
        blob: null,
      }).then((attachmentModel) => {
        if (attachmentModel.url) {
          setAttachmentUrl(attachmentModel.url);
        } else if (attachmentModel.blob) {
          const url = URL.createObjectURL(attachmentModel.blob);
          setObjectUrl(url);
          setAttachmentUrl(url);
        }
      });
    }
  }, [attachment, setAttachmentUrl, setObjectUrl]);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const linkifyDecorator = (href: string, text: string, key: number) => (
    <a href={href} key={key} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );

  const renderTitle = () => {
    if (alertVital) {
      return (
        <h6 className="cb-title">
          <FormattedMessage id={`vital_alert.${alertVital.toLowerCase()}`} />
        </h6>
      );
    }
    return isMe || isSequence ? null : <h6 className="cb-title">{from}</h6>;
  };

  const renderAvatar = () => {
    if (alertVital) {
      return <div className="cb-avatar">{getIcon(alertVital)}</div>;
    }
    return (
      <div className="cb-avatar">{isSequence ? null : <Avatar size={32} imgUrl={avatarUrl} />}</div>
    );
  };

  const openAttachment = (imgUrl: string) => {
    dispatch(
      appActions.openModal({
        id: 'IMAGE',
        data: {
          imgUrl: imgUrl,
          caption: text === 'attachment' ? '' : text,
        },
      }),
    );
  };

  const renderAttachment = () => {
    if (attachmentUrl && attachment && attachment.type?.includes('image')) {
      return (
        <img
          className="cb-attachment"
          onClick={() => openAttachment(attachmentUrl)}
          src={attachmentUrl}
          alt=""
        />
      );
    }
    if (attachmentUrl && attachment) {
      return (
        <a
          download={`${attachment.name}`}
          href={attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {attachment.name}
        </a>
      );
    }
  };

  return (
    <ChatBubbleSC isMe={isMe} isSequence={isSequence} styleType={styleType}>
      {renderAvatar()}
      <div className="cb-message">
        {renderTitle()}
        {text !== 'attachment' ? (
          <p>
            <Linkify componentDecorator={linkifyDecorator}>{text.trim()}</Linkify>
          </p>
        ) : null}
        {attachment ? renderAttachment() : null}
        {stamp ? (
          <time className="cb-time">{moment(stamp).format(getTimeFormat(false))}</time>
        ) : null}
      </div>
    </ChatBubbleSC>
  );
};

export default React.memo(ChatBubble);
